import { isMealPlanAddOn, isMealPlanMain, isMealPlanSideOrStaple } from 'common/MealPlanUtils';
import { DateString } from 'common/UrlDate';
import { cloneDeep, keyBy, omit } from 'lodash';
import { MealPlanDisplayFoodItemFragment, MealPlanItem, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { MealPlanFoodItem } from 'src/pages/consumer/meal-plans/checkout/types';
import { getQuantityInUnits } from 'src/pages/consumer/meal-plans/utils/cartServingsConversion';
import { create, StoreApi } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createSelectors } from './create-selectors';

export type MealPlanCartItem = {
  foodItem: MealPlanDisplayFoodItemFragment;
  quantity: number;
};

export type CartForShef = {
  cartItems: MealPlanCartItem[];
  shef: MealPlanShefFragment;
};

export type Cart = {
  [shefId: string]: CartForShef;
};

// Define the interface of the Cart state
interface State {
  preEditCart: Cart;
  cart: Cart;
  lastAddedMain: MealPlanFoodItem | null;
  cartIsShowing: boolean;
  deliveryDate: DateString | null;
  cartAnalyticsLocation: string;
  menuIsLoading: boolean;
  mealsPerWeek: number;
  numServings: number;
  mealPlanTypeId: number;
}

interface UpsellPriceBreakdown {
  totalCost: number;
  itemCount: number;
}
// Define the interface of the actions that can be performed in the Cart
interface Actions {
  increaseCartQuantity: (item: MealPlanCartItem, shef: MealPlanShefFragment) => void;
  decreaseCartQuantity: (item: MealPlanCartItem) => void;
  clearAllCart: () => void;
  clearLastAddedMain: () => void;
  clearCart: () => void;
  clearPreEditCart: () => void;
  setItemQuantity: (item: MealPlanCartItem, shef: MealPlanShefFragment) => void;
  setPreEditCart: (preEditCart: Cart) => void;
  setCart: (cart: Cart) => void;
  setCartAnalyticsLocation: (cartAnalyticsLocation: string) => void;
  /** TODO: Remove these after getting rid of sides */
  getCartAnalyticsLocation: () => string;
  getCartForShef: (shefId: string) => CartForShef | undefined;
  getShefIds: () => string[];
  getCartForMainShefs: () => CartForShef[];
  getCartForSideShefs: () => CartForShef[];
  getCartForAddOnShefs: () => CartForShef[];
  getPreEditCartForMainShefs: () => CartForShef[];
  getPreEditCartForShef: (shefId: string) => CartForShef | undefined;
  getNumItemsInCart: () => number;
  getNumSelectedMains: () => number;
  getNumSelectedServings: () => number;
  getFlexServingsCounts: () => { mainServings: number; extraServings: number; uniqueMains: number };
  getNumSelectedSides: () => number;
  getNumSelectedAddOns: () => number;
  getUpsellsTotalExtraAmountFromCart: () => number;
  /** Counts upgrades by the serving for flex, and control mains by the meal. */
  getUpsellPriceBreakdown: (numServings: number, isFlexServings: boolean) => UpsellPriceBreakdown;
  getLineItemsFromCart: () => MealPlanItem[];
  reconcileCart: (
    cartItems: MealPlanItem[],
    numServings: number
  ) => {
    hasMadeChanges: boolean;
    removedFoodItemIds: number[];
    validatedFoodItemIds: number[];
  };
  showCart: () => void;
  hideCart: () => void;
  toggleCart: () => void;
  setDeliveryDate: (deliveryDate: DateString) => void;
  clearDeliveryDate: () => void;
  setMenuIsLoading: (menuIsLoading: boolean) => void;
  setMealPlanConfig: (mealsPerWeek: number, numServings: number, mealPlanTypeId: number) => void;
  clearMealPlanConfig: () => void;
  getNumRemainingMains: () => number;
}

// Initialize a default state
export const INITIAL_STATE: State = {
  preEditCart: {},
  cart: {},
  lastAddedMain: null,
  cartIsShowing: false,
  deliveryDate: null,
  cartAnalyticsLocation: 'unknown',
  menuIsLoading: false,
  mealsPerWeek: 1,
  numServings: 1,
  mealPlanTypeId: 0,
};

const getCartsForShefsByFoodType = (cart: Cart, filterFn: (foodItem: MealPlanFoodItem) => boolean) =>
  Object.values(cart)
    .map((cartForShef) => ({
      ...cartForShef,
      cartItems: cartForShef.cartItems.filter((mpCartItem) => filterFn(mpCartItem.foodItem)),
    }))
    .filter((cartForShef) => cartForShef.cartItems.length > 0);

const setLastAddedMain = (set: (partial: Partial<State>) => void, foodItem: MealPlanFoodItem) => {
  const isMainDish = isMealPlanMain(foodItem);
  set({ lastAddedMain: isMainDish ? foodItem : null });
};

const updateCartState = (
  set: (partial: Partial<State>) => void,
  get: StoreApi<State & Actions>['getState'],
  shefId: string,
  cartForShef: { cartItems: MealPlanCartItem[]; shef: MealPlanShefFragment },
  updatedCartItems: MealPlanCartItem[]
) => {
  if (updatedCartItems.length > 0) {
    set({ cart: { ...get().cart, [shefId]: { ...cartForShef, cartItems: updatedCartItems } } });
  } else {
    set({ cart: omit(get().cart, shefId) });
  }
};

const mergeCartItems = (existing: MealPlanCartItem, incoming: MealPlanCartItem): MealPlanCartItem => ({
  ...existing,
  quantity: existing.quantity + incoming.quantity,
});

export const useCartStore = create(
  persist(
    immer<State & Actions>((set, get) => ({
      ...INITIAL_STATE,
      increaseCartQuantity: (cartItem: MealPlanCartItem, shef: MealPlanShefFragment) => {
        const { cart } = get();
        const shefId = shef.id;
        const cartForShef = cart[shefId] || { cartItems: [], shef };

        const existingItemIndex = cartForShef.cartItems.findIndex((item) => item.foodItem.id === cartItem.foodItem.id);

        if (existingItemIndex === -1 && isMealPlanMain(cartItem.foodItem)) {
          setLastAddedMain(set, cartItem.foodItem);
        }

        const updatedCartItems =
          existingItemIndex !== -1
            ? cartForShef.cartItems.map((item, index) =>
                index === existingItemIndex ? mergeCartItems(item, cartItem) : item
              )
            : [...cartForShef.cartItems, cartItem];

        updateCartState(set, get, shefId, cartForShef, updatedCartItems);
      },
      decreaseCartQuantity: (product: MealPlanCartItem) => {
        const { cart } = get();
        const shefId = product.foodItem.shef.id;
        const cartForShef = cart[shefId];

        if (!cartForShef) return;

        const updatedCartItems = cartForShef.cartItems
          .map((item) =>
            item.foodItem.id === product.foodItem.id ? { ...item, quantity: item.quantity - product.quantity } : item
          )
          .filter((item) => item.quantity > 0);
        const productStillInCart = updatedCartItems.some((item) => item.foodItem.id === product.foodItem.id);

        updateCartState(set, get, shefId, cartForShef, updatedCartItems);
        if (!productStillInCart && isMealPlanMain(product.foodItem)) {
          set({ lastAddedMain: null });
        }
      },
      clearAllCart: () => {
        set(INITIAL_STATE);
      },
      clearCart: () => {
        set({ cart: INITIAL_STATE.cart });
      },
      clearLastAddedMain: () => {
        set({ lastAddedMain: null });
      },
      clearPreEditCart: () => {
        set({ preEditCart: INITIAL_STATE.preEditCart });
      },
      setItemQuantity: (cartItem: MealPlanCartItem, shef: MealPlanShefFragment) => {
        const { cart } = get();
        const shefId = shef.id;
        const cartForShef = cart[shefId] || { cartItems: [], shef };

        const itemExists = cartForShef.cartItems.some((item) => item.foodItem.id === cartItem.foodItem.id);

        if (!itemExists && isMealPlanMain(cartItem.foodItem)) {
          setLastAddedMain(set, cartItem.foodItem);
        }

        const updatedCartItems = itemExists
          ? cartForShef.cartItems
              .map((item) =>
                item.foodItem.id === cartItem.foodItem.id ? { ...item, quantity: cartItem.quantity } : item
              )
              .filter((item) => item.quantity > 0)
          : [...cartForShef.cartItems, cartItem];

        updateCartState(set, get, shefId, cartForShef, updatedCartItems);
      },
      setPreEditCart: (preEditCart: Cart) => {
        const mergeCart = Object.entries(preEditCart).reduce((acc, [shefId, cartForShef]) => {
          const filteredCartItems = cartForShef.cartItems.filter((item) => item.quantity > 0);
          if (filteredCartItems.length > 0) {
            return { ...acc, [shefId]: { ...cartForShef, cartItems: filteredCartItems } };
          }
          return acc;
        }, {});
        set({ preEditCart: mergeCart });
        // Important: ensure cart and preEditCart do NOT share references, or edits will mutate both
        set({ cart: cloneDeep(mergeCart) });
      },
      setCart: (cart: Cart) => {
        set({ cart });
      },
      setCartAnalyticsLocation: (cartAnalyticsLocation: string) => {
        set({ cartAnalyticsLocation });
      },
      getCartAnalyticsLocation: () => {
        const { cartAnalyticsLocation } = get();
        return cartAnalyticsLocation;
      },
      getCartForShef: (shefId: string) => {
        const { cart } = get();
        return cart[shefId];
      },
      getShefIds: () => Object.keys(get().cart),
      getCartForMainShefs: () => {
        const { cart } = get();
        return getCartsForShefsByFoodType(cart, isMealPlanMain);
      },
      getCartForSideShefs: () => {
        const { cart } = get();
        return getCartsForShefsByFoodType(cart, isMealPlanSideOrStaple);
      },
      getCartForAddOnShefs: () => {
        const { cart } = get();
        return getCartsForShefsByFoodType(cart, isMealPlanAddOn);
      },
      getPreEditCartForMainShefs: () => {
        const { preEditCart } = get();
        return getCartsForShefsByFoodType(preEditCart, isMealPlanMain);
      },
      getPreEditCartForShef: (shefId: string) => {
        const { preEditCart } = get();
        return preEditCart[shefId];
      },
      getNumItemsInCart: () => {
        const { cart, numServings } = get();
        return Object.values(cart).reduce(
          (acc, shef) =>
            acc + shef.cartItems.reduce((itemAcc, item) => itemAcc + getQuantityInUnits(item, numServings), 0),
          0
        );
      },
      getNumSelectedMains: () => {
        const { cart, numServings } = get();
        const shefsWithMains = getCartsForShefsByFoodType(cart, isMealPlanMain);
        return shefsWithMains.reduce(
          (acc, shef) =>
            acc + shef.cartItems.reduce((itemAcc, item) => itemAcc + getQuantityInUnits(item, numServings), 0),
          0
        );
      },
      getNumSelectedServings: () => {
        const { cart } = get();
        const shefsWithMains = getCartsForShefsByFoodType(cart, isMealPlanMain);
        return shefsWithMains.reduce(
          (acc, shef) => acc + shef.cartItems.reduce((itemAcc, item) => itemAcc + item.quantity, 0),
          0
        );
      },
      /**
       * Returns the plan's base main servings (`mainServings` = unit * numServings) and any main
       * servings in the cart beyond that base (`extraServings`).
       */
      getFlexServingsCounts: () => {
        const { cart, mealsPerWeek, numServings } = get();
        const mainServings = mealsPerWeek * numServings;
        const totalMainServingsInCart = get().getNumSelectedServings();
        const uniqueMains = new Set(
          getCartsForShefsByFoodType(cart, isMealPlanMain).flatMap((shef) =>
            shef.cartItems.map((item) => item.foodItem.id)
          )
        ).size;
        return {
          mainServings,
          extraServings: Math.max(0, totalMainServingsInCart - mainServings),
          uniqueMains,
        };
      },
      getNumSelectedSides: () => {
        const { cart } = get();
        const shefsWithSides = getCartsForShefsByFoodType(cart, isMealPlanSideOrStaple);
        return shefsWithSides.reduce(
          (acc, shef) => acc + shef.cartItems.reduce((itemAcc, item) => itemAcc + item.quantity, 0),
          0
        );
      },
      getNumSelectedAddOns: () => {
        const { cart } = get();
        const shefsWithAddOns = getCartsForShefsByFoodType(cart, isMealPlanAddOn);
        return shefsWithAddOns.reduce(
          (acc, shef) => acc + shef.cartItems.reduce((itemAcc, item) => itemAcc + item.quantity, 0),
          0
        );
      },
      getUpsellsTotalExtraAmountFromCart: () => {
        const { cart } = get();
        const cartForMains = getCartsForShefsByFoodType(cart, isMealPlanMain);
        return Object.values(cartForMains).reduce(
          (acc, shef) =>
            acc +
            shef.cartItems.reduce((itemAcc, item) => itemAcc + (item.foodItem.upsell?.price ?? 0) * item.quantity, 0),
          0
        );
      },
      getUpsellPriceBreakdown: (numServings: number, isFlexServings: boolean) => {
        const { cart } = get();
        const cartForMains = getCartsForShefsByFoodType(cart, isMealPlanMain);
        return Object.values(cartForMains).reduce<UpsellPriceBreakdown>(
          (acc, shef) =>
            shef.cartItems.reduce<UpsellPriceBreakdown>((itemAcc, item) => {
              const upsellTotalCost = (item.foodItem.upsell?.price ?? 0) * item.quantity;
              if (upsellTotalCost <= 0) return itemAcc;

              const { totalCost = 0, itemCount = 0 } = itemAcc ?? {};
              return {
                totalCost: totalCost + upsellTotalCost,
                itemCount: itemCount + (isFlexServings ? item.quantity : getQuantityInUnits(item, numServings)),
              };
            }, acc),
          { totalCost: 0, itemCount: 0 }
        );
      },
      getLineItemsFromCart: () => {
        const { cart } = get();

        return Object.values(cart).reduce<MealPlanItem[]>((acc: MealPlanItem[], cartForShef: CartForShef) => {
          const items = cartForShef.cartItems.map((item) => ({
            quantity: item.quantity,
            foodItemId: item.foodItem.id,
            shefId: item.foodItem.shef.id,
          }));
          return [...acc, ...items];
        }, []);
      },
      reconcileCart: (cartItems: MealPlanItem[], numServings) => {
        const { cart } = get();

        const cartHasItems = Object.values(cart).length > 0;

        if (!numServings || numServings <= 0) {
          set({ cart: INITIAL_STATE.cart });
          return { hasMadeChanges: cartHasItems, removedFoodItemIds: [], validatedFoodItemIds: [] };
        }

        const cartItemsByFoodItemId = keyBy(cartItems, 'foodItemId');

        type ValidationAcc = {
          validatedCart: CartForShef[];
          removedFoodItemIds: number[];
          hasMadeChanges: boolean;
        };

        const { validatedCart, removedFoodItemIds, hasMadeChanges } = Object.values(cart).reduce<ValidationAcc>(
          (cartForShefAcc, cartForShef) => {
            const { shef } = cartForShef;
            const {
              items: updatedCartForShef,
              removedIds,
              hadChanges,
            } = cartForShef.cartItems.reduce<{
              items: MealPlanCartItem[];
              removedIds: number[];
              hadChanges: boolean;
            }>(
              (acc, cartItem) => {
                const validCartItem = cartItemsByFoodItemId[cartItem.foodItem.id];

                if (!validCartItem) {
                  return {
                    items: acc.items,
                    removedIds: [...acc.removedIds, cartItem.foodItem.id],
                    hadChanges: true,
                  };
                }

                // Both sides are in servings; write back the server quantity when it differs.
                if (cartItem.quantity !== validCartItem.quantity) {
                  if (validCartItem.quantity <= 0) {
                    return {
                      items: acc.items,
                      removedIds: [...acc.removedIds, cartItem.foodItem.id],
                      hadChanges: true,
                    };
                  }
                  return {
                    items: [...acc.items, { ...cartItem, quantity: validCartItem.quantity }],
                    removedIds: acc.removedIds,
                    hadChanges: true,
                  };
                }

                return {
                  items: [...acc.items, cartItem],
                  removedIds: acc.removedIds,
                  hadChanges: acc.hadChanges,
                };
              },
              { items: [], removedIds: [], hadChanges: false }
            );

            if (!updatedCartForShef.length) {
              return {
                ...cartForShefAcc,
                removedFoodItemIds: [...cartForShefAcc.removedFoodItemIds, ...removedIds],
                hasMadeChanges: cartForShefAcc.hasMadeChanges || hadChanges,
              };
            }
            return updatedCartForShef.length
              ? {
                  validatedCart: [...cartForShefAcc.validatedCart, { shef, cartItems: updatedCartForShef }],
                  removedFoodItemIds: [...cartForShefAcc.removedFoodItemIds, ...removedIds],
                  hasMadeChanges: cartForShefAcc.hasMadeChanges || hadChanges,
                }
              : {
                  ...cartForShefAcc,
                  removedFoodItemIds: [...cartForShefAcc.removedFoodItemIds, ...removedIds],
                  hasMadeChanges: cartForShefAcc.hasMadeChanges || hadChanges,
                };
          },
          { validatedCart: [], removedFoodItemIds: [], hasMadeChanges: false }
        );

        const cartToSet = keyBy(validatedCart, 'shef.id');
        set({ cart: cartToSet });
        const validatedFoodItemIds = validatedCart.flatMap((cartForShef) =>
          cartForShef.cartItems.map((item) => item.foodItem.id)
        );
        return { hasMadeChanges, removedFoodItemIds, validatedFoodItemIds };
      },
      showCart: () => {
        set({ cartIsShowing: true });
      },
      hideCart: () => {
        set({ cartIsShowing: false });
      },
      toggleCart: () => {
        set({ cartIsShowing: !get().cartIsShowing });
      },
      setDeliveryDate: (deliveryDate: DateString) => {
        set({ deliveryDate });
      },
      clearDeliveryDate: () => {
        set({ deliveryDate: null });
      },
      setMenuIsLoading: (menuIsLoading: boolean) => {
        set({ menuIsLoading });
      },
      setMealPlanConfig: (mealsPerWeek: number, numServings: number, mealPlanTypeId: number) => {
        set({ mealsPerWeek, numServings, mealPlanTypeId });
      },
      clearMealPlanConfig: () => {
        set({ mealsPerWeek: 0, numServings: 0, mealPlanTypeId: 0 });
      },
      getNumRemainingMains: () => {
        const { mealsPerWeek, getNumSelectedMains } = get();
        return Math.max(0, mealsPerWeek - getNumSelectedMains());
      },
    })),
    {
      name: 'cart-storage', // unique name
      getStorage: () => localStorage,
    }
  )
);

export const useCartStoreSelectors = createSelectors(useCartStore);
